// Hand-maintained — override generation
import {
  // Outbound emails
  getAdminEmailOutboundEmailsById,
  getAdminEmailOutboundEmailsWorkspaceByWorkspaceId,
  getAdminEmailOutboundEmailsWorkspaceByWorkspaceIdContactByContactRefId,
  postAdminEmailOutboundEmails,
  postAdminEmailOutboundEmailsComposeWithAi,
  patchAdminEmailOutboundEmailsById,
  deleteAdminEmailOutboundEmailsById,
  patchAdminEmailOutboundEmailsByIdSend,
  patchAdminEmailOutboundEmailsByIdSchedule,
  patchAdminEmailOutboundEmailsByIdCancelSchedule,
  // Sender profiles
  getAdminEmailMarketingSenderProfilesById,
  getAdminEmailMarketingSenderProfilesWorkspaceByWorkspaceId,
  postAdminEmailMarketingSenderProfiles,
  patchAdminEmailMarketingSenderProfilesById,
  deleteAdminEmailMarketingSenderProfilesById,
  patchAdminEmailMarketingSenderProfilesByIdValidateDns,
  patchAdminEmailMarketingSenderProfilesByIdSetDefault,
  // Tracking events
  getAdminEmailTrackingEventsById,
  getAdminEmailTrackingEventsWorkspaceByWorkspaceId,
  // Unsubscribers
  getAdminEmailMarketingUnsubscribersById,
  getAdminEmailMarketingUnsubscribersWorkspaceByWorkspaceId,
  // Recipients
  getAdminEmailRecipientsById,
  getAdminEmailRecipientsEmailByOutboundEmailId,
  postAdminEmailRecipients,
  deleteAdminEmailRecipientsById,
  // Inclusions
  getAdminEmailInclusionsById,
  getAdminEmailInclusionsEmailByOutboundEmailId,
  postAdminEmailInclusions,
  patchAdminEmailInclusionsById,
  deleteAdminEmailInclusionsById,
  // Inbound addresses
  getAdminEmailInboundAddressesById,
  getAdminEmailInboundAddressesWorkspaceByWorkspaceId,
  postAdminEmailInboundAddresses,
  patchAdminEmailInboundAddressesById,
  deleteAdminEmailInboundAddressesById,
  patchAdminEmailInboundAddressesByIdRotateToken,
  patchAdminEmailInboundAddressesByIdDisable,
  patchAdminEmailInboundAddressesByIdEnable,
  // Inbound received
  getAdminEmailInboundReceivedById,
  getAdminEmailInboundReceivedWorkspaceByWorkspaceId,
  getAdminEmailInboundReceivedAddressByInboundAddressId,
  // Send limits
  getAdminEmailSendLimitsById,
  getAdminEmailSendLimitsWorkspaceByWorkspaceId,
} from "../_internal/sdk.gen";
import type {
  EmailOutboundEmail,
  EmailMarketingSenderProfile,
  EmailTrackingEvent,
  EmailUnsubscriber,
  EmailRecipient,
  EmailInclusion,
  EmailInboundAddress,
  EmailInboundEmail,
} from "../_internal/types.gen";
import type { RequestOptions } from "../base-client";
import { buildPageQuery } from "../namespace-types";
import { RequestBuilder } from "../request-builder";

// ---------------------------------------------------------------------------
// Shared value types
// ---------------------------------------------------------------------------

/** Context reference type — links an email to a domain entity for history tracking. */
export type ContextRefType = "custom" | "deal" | "session" | "ticket";

/** Inclusion content category. */
export type InclusionType =
  | "chart"
  | "custom"
  | "goal"
  | "metric"
  | "resource"
  | "supplement";

/** Recipient role on an outbound email. */
export type RecipientType = "to" | "cc" | "bcc";

/** Inbound email routing target. */
export type InboundRoutingTarget = "extraction" | "agent" | "invoices" | "crm";

/**
 * A file attachment on an outbound email.
 *
 * Upload the file to platform storage first, then reference its
 * `storage_key` here. Maximum 10 attachments per email.
 */
export interface EmailAttachment {
  /** Platform storage key returned by the upload API. */
  storage_key: string;
  /** Display filename shown to the recipient. */
  filename: string;
  /** MIME type (e.g. `"application/pdf"`). */
  content_type: string;
}

// ---------------------------------------------------------------------------
// Outbound email types
// ---------------------------------------------------------------------------

/** Daily send quota record for a workspace. */
export interface EmailSendLimit {
  id: string;
  date: string;
  workspace_id: string;
  limit_with_unsubscribe: number;
  limit_without_unsubscribe: number;
  sent_with_unsubscribe: number;
  sent_without_unsubscribe: number;
  remaining_with_unsubscribe?: number;
  remaining_without_unsubscribe?: number;
  inserted_at: string;
  updated_at: string;
}

/**
 * Attributes for creating an outbound email draft.
 *
 * Workspace is resolved from the authenticated actor — do not pass
 * `workspace_id`.
 */
export interface CreateOutboundEmailAttributes {
  /** Email subject line. Required. */
  subject: string;
  /** HTML body content. */
  body_html?: string;
  /** Plain-text body (fallback for non-HTML clients). */
  body_text?: string;
  /** Sender profile to use. Falls back to workspace default if omitted. */
  sender_profile_id?: string;
  /** CRM contact reference for tracking. */
  contact_ref_id?: string;
  /** ID of the linked domain entity. */
  context_ref_id?: string;
  /** Type of the linked domain entity. */
  context_ref_type?: ContextRefType;
  /** Append a CAN-SPAM unsubscribe link to the email footer. */
  include_unsubscribe_link?: boolean;
  /** File attachments (max 10). */
  attachments?: EmailAttachment[];
}

/**
 * Attributes for updating an outbound email (PATCH semantics).
 *
 * Only emails in `draft` status can be updated.
 */
export interface UpdateOutboundEmailAttributes {
  /** Updated subject line. */
  subject?: string;
  /** Updated HTML body. */
  body_html?: string;
  /** Updated plain-text body. */
  body_text?: string;
  /** Change the sender profile. */
  sender_profile_id?: string;
  /** Toggle unsubscribe link. */
  include_unsubscribe_link?: boolean;
  /** Replace the attachment list. */
  attachments?: EmailAttachment[];
}

// ---------------------------------------------------------------------------
// Sender profile types
// ---------------------------------------------------------------------------

/**
 * Attributes for creating a sender profile.
 *
 * Workspace is resolved from the authenticated actor — do not pass
 * `workspace_id`.
 */
export interface CreateSenderProfileAttributes {
  /** Sender email address. Must be unique per workspace. */
  email: string;
  /** Display name shown in the From header. */
  name: string;
  /** Physical mailing address (CAN-SPAM compliance). */
  address?: string;
  /** Company name for the email footer. */
  company?: string;
  /** Set as the workspace default on creation. */
  is_default?: boolean;
  /** Contact phone number. */
  phone?: string;
  /** HTML signature block. */
  signature?: string;
}

/** Attributes for updating a sender profile (PATCH semantics). */
export interface UpdateSenderProfileAttributes {
  /** Updated display name. */
  name?: string;
  /** Updated mailing address. */
  address?: string;
  /** Updated company name. */
  company?: string;
  /** DKIM selector override. */
  dkim_selector?: string;
  /** Change default status. */
  is_default?: boolean;
  /** Updated phone number. */
  phone?: string;
  /** Updated HTML signature. */
  signature?: string;
}

// ---------------------------------------------------------------------------
// Recipient types
// ---------------------------------------------------------------------------

/** Attributes for adding a recipient to an outbound email. */
export interface CreateRecipientAttributes {
  /** ID of the outbound email. */
  outbound_email_id: string;
  /** Recipient email address. */
  email: string;
  /** Recipient display name. */
  name?: string;
  /** Role: primary (`to`), carbon copy (`cc`), or blind copy (`bcc`). */
  recipient_type?: RecipientType;
}

// ---------------------------------------------------------------------------
// Inclusion types
// ---------------------------------------------------------------------------

/** Attributes for attaching a structured data inclusion to a draft email. */
export interface CreateInclusionAttributes {
  /** ID of the outbound email. */
  outbound_email_id: string;
  /** Content category. */
  inclusion_type: InclusionType;
  /** Platform object ID referenced by this inclusion. */
  ref_id?: string;
  /** Display title. */
  title?: string;
  /** Structured payload (shape depends on `inclusion_type`). */
  data?: Record<string, unknown>;
  /** Ordering position (0-indexed). */
  position?: number;
}

/** Attributes for updating an inclusion (PATCH semantics). */
export interface UpdateInclusionAttributes {
  /** Updated display title. */
  title?: string;
  /** Pre-rendered HTML. */
  rendered_html?: string;
  /** Updated structured payload. */
  data?: Record<string, unknown>;
  /** Updated ordering position. */
  position?: number;
}

// ---------------------------------------------------------------------------
// Inbound address types
// ---------------------------------------------------------------------------

/**
 * Attributes for creating an inbound address.
 *
 * Unlike outbound resources, `workspace_id` is an explicit argument here.
 */
export interface CreateInboundAddressAttributes {
  /** Workspace to create the address in. */
  workspace_id: string;
  /** Human-readable label. */
  name: string;
  /** Where received emails are routed. */
  routing_target: InboundRoutingTarget;
  /** Routing-target-specific configuration. */
  handler_config?: Record<string, unknown>;
  /** Max attachment size in MB (1–100, default 25). */
  max_attachment_size_mb?: number;
  /** Restrict accepted MIME types. `null` allows all. */
  allowed_mime_types?: string[] | null;
  /** Max emails per day (1–10,000, default 100). */
  daily_receive_limit?: number;
}

/** Attributes for updating an inbound address (PATCH semantics). */
export interface UpdateInboundAddressAttributes {
  /** Updated label. */
  name?: string;
  /** Change routing target. */
  routing_target?: InboundRoutingTarget;
  /** Updated routing configuration. */
  handler_config?: Record<string, unknown>;
  /** Updated max attachment size in MB. */
  max_attachment_size_mb?: number;
  /** Updated MIME type restrictions. */
  allowed_mime_types?: string[] | null;
  /** Updated daily receive limit. */
  daily_receive_limit?: number;
}

// ---------------------------------------------------------------------------
// AI composition types
// ---------------------------------------------------------------------------

/**
 * Parameters for AI-assisted email composition.
 *
 * Workspace is resolved from the authenticated actor — do not pass it.
 */
export interface ComposeWithAiAttributes {
  /** Email addresses of primary recipients. */
  to: string[];
  /** AI drafting instructions. */
  prompt: string;
  /** Structured context data passed to the AI. */
  context?: Record<string, unknown>;
  /** Sender profile ID — uses workspace default if omitted. */
  sender_profile_id?: string;
  /** CRM contact reference. */
  contact_ref_id?: string;
  /** Link the composed email to a domain entity. */
  context_ref_type?: ContextRefType;
  /** ID of the linked domain entity. */
  context_ref_id?: string;
  /** Include a CAN-SPAM unsubscribe link. */
  include_unsubscribe_link?: boolean;
}

// ---------------------------------------------------------------------------
// Namespace factory
// ---------------------------------------------------------------------------

/**
 * Admin-level email namespace — full cross-workspace email management.
 *
 * Provides ISV administrators with complete outbound email lifecycle,
 * sender profile management, recipient/inclusion CRUD, inbound address
 * routing, and tracking event visibility across all workspaces.
 */
export function createEmailNamespace(rb: RequestBuilder) {
  return {
    /**
     * Outbound Emails — full lifecycle management.
     */
    outboundEmails: {
      /**
       * Get an outbound email by ID.
       *
       * @param id - The outbound email ID.
       * @param options - Optional request-level overrides.
       * @returns The requested {@link EmailOutboundEmail}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailOutboundEmail> => {
        return rb.execute<EmailOutboundEmail>(
          getAdminEmailOutboundEmailsById,
          { path: { id } },
          options,
        );
      },

      /**
       * List outbound emails for a workspace.
       *
       * @param workspaceId - The workspace ID.
       * @param options - Pagination and request-level overrides.
       * @returns An array of {@link EmailOutboundEmail} records.
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<EmailOutboundEmail[]> => {
        return rb.execute<EmailOutboundEmail[]>(
          getAdminEmailOutboundEmailsWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            ...buildPageQuery(options?.page, options?.pageSize),
          },
          options,
        );
      },

      /**
       * List emails sent to a specific contact within a workspace.
       *
       * @param workspaceId - The workspace ID.
       * @param contactRefId - The CRM contact reference ID.
       * @param options - Pagination and request-level overrides.
       * @returns An array of {@link EmailOutboundEmail} records.
       */
      listByContact: async (
        workspaceId: string,
        contactRefId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<EmailOutboundEmail[]> => {
        return rb.execute<EmailOutboundEmail[]>(
          getAdminEmailOutboundEmailsWorkspaceByWorkspaceIdContactByContactRefId,
          {
            path: { workspace_id: workspaceId, contact_ref_id: contactRefId },
            ...buildPageQuery(options?.page, options?.pageSize),
          },
          options,
        );
      },

      /**
       * Create an outbound email draft.
       *
       * Workspace is resolved from the authenticated actor.
       *
       * @param attributes - Email attributes. `subject` is required.
       * @param options - Optional request-level overrides.
       * @returns The newly created {@link EmailOutboundEmail} draft.
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       * const draft = await admin.email.outboundEmails.create({
       *   subject: 'Admin notification',
       *   body_html: '<p>Hello</p>',
       *   sender_profile_id: 'sp_noreply',
       * });
       * ```
       */
      create: async (
        attributes: CreateOutboundEmailAttributes,
        options?: RequestOptions,
      ): Promise<EmailOutboundEmail> => {
        return rb.execute<EmailOutboundEmail>(
          postAdminEmailOutboundEmails,
          { body: { data: { type: "email-outbound-email", attributes } } },
          options,
        );
      },

      /**
       * AI-draft an email from a prompt and structured context.
       *
       * @param attributes - AI composition parameters. `to` and `prompt` are required.
       * @param options - Optional request-level overrides.
       * @returns The AI-generated {@link EmailOutboundEmail} draft.
       */
      composeWithAi: async (
        attributes: ComposeWithAiAttributes,
        options?: RequestOptions,
      ): Promise<EmailOutboundEmail> => {
        return rb.execute<EmailOutboundEmail>(
          postAdminEmailOutboundEmailsComposeWithAi,
          { body: { data: { type: "email-outbound-email", attributes } } },
          options,
        );
      },

      /**
       * Update a draft outbound email (PATCH semantics).
       *
       * Only emails in `draft` status can be updated.
       *
       * @param id - The outbound email ID.
       * @param attributes - Fields to update.
       * @param options - Optional request-level overrides.
       * @returns The updated {@link EmailOutboundEmail}.
       */
      update: async (
        id: string,
        attributes: UpdateOutboundEmailAttributes,
        options?: RequestOptions,
      ): Promise<EmailOutboundEmail> => {
        return rb.execute<EmailOutboundEmail>(
          patchAdminEmailOutboundEmailsById,
          {
            path: { id },
            body: { data: { id, type: "email-outbound-email", attributes } },
          },
          options,
        );
      },

      /**
       * Delete a draft outbound email.
       *
       * @param id - The outbound email ID.
       * @param options - Optional request-level overrides.
       * @returns `true` on successful deletion.
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminEmailOutboundEmailsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Send a draft email immediately.
       *
       * @param id - The outbound email ID.
       * @param options - Optional request-level overrides.
       * @returns The email in `ready` status.
       */
      send: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailOutboundEmail> => {
        return rb.execute<EmailOutboundEmail>(
          patchAdminEmailOutboundEmailsByIdSend,
          {
            path: { id },
            body: { data: { id, type: "email-outbound-email" } },
          },
          options,
        );
      },

      /**
       * Schedule a draft for future delivery.
       *
       * @param id - The outbound email ID.
       * @param attributes - Must include `scheduled_for` as ISO 8601 datetime.
       * @param options - Optional request-level overrides.
       * @returns The email in `scheduled` status.
       */
      schedule: async (
        id: string,
        attributes: { scheduled_for: string },
        options?: RequestOptions,
      ): Promise<EmailOutboundEmail> => {
        return rb.execute<EmailOutboundEmail>(
          patchAdminEmailOutboundEmailsByIdSchedule,
          {
            path: { id },
            body: { data: { id, type: "email-outbound-email", attributes } },
          },
          options,
        );
      },

      /**
       * Cancel a scheduled email (returns to `draft` status).
       *
       * @param id - The outbound email ID.
       * @param options - Optional request-level overrides.
       * @returns The email back in `draft` status.
       */
      cancelSchedule: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailOutboundEmail> => {
        return rb.execute<EmailOutboundEmail>(
          patchAdminEmailOutboundEmailsByIdCancelSchedule,
          {
            path: { id },
            body: { data: { id, type: "email-outbound-email" } },
          },
          options,
        );
      },
    },

    /**
     * Sender Profiles — verified from-address identities.
     */
    senderProfiles: {
      /**
       * Get a sender profile by ID.
       *
       * @param id - The sender profile ID.
       * @param options - Optional request-level overrides.
       * @returns The requested {@link EmailMarketingSenderProfile}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailMarketingSenderProfile> => {
        return rb.execute<EmailMarketingSenderProfile>(
          getAdminEmailMarketingSenderProfilesById,
          { path: { id } },
          options,
        );
      },

      /**
       * List sender profiles for a workspace.
       *
       * @param workspaceId - The workspace ID.
       * @param options - Pagination and request-level overrides.
       * @returns An array of {@link EmailMarketingSenderProfile} records.
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<EmailMarketingSenderProfile[]> => {
        return rb.execute<EmailMarketingSenderProfile[]>(
          getAdminEmailMarketingSenderProfilesWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            ...buildPageQuery(options?.page, options?.pageSize),
          },
          options,
        );
      },

      /**
       * Create a new sender profile.
       *
       * Workspace is resolved from the authenticated actor.
       *
       * @param attributes - Profile attributes. `email` and `name` are required.
       * @param options - Optional request-level overrides.
       * @returns The newly created {@link EmailMarketingSenderProfile}.
       */
      create: async (
        attributes: CreateSenderProfileAttributes,
        options?: RequestOptions,
      ): Promise<EmailMarketingSenderProfile> => {
        return rb.execute<EmailMarketingSenderProfile>(
          postAdminEmailMarketingSenderProfiles,
          {
            body: {
              data: { type: "email-marketing-sender-profile", attributes },
            },
          },
          options,
        );
      },

      /**
       * Update a sender profile (PATCH semantics).
       *
       * @param id - The sender profile ID.
       * @param attributes - Fields to update.
       * @param options - Optional request-level overrides.
       * @returns The updated {@link EmailMarketingSenderProfile}.
       */
      update: async (
        id: string,
        attributes: UpdateSenderProfileAttributes,
        options?: RequestOptions,
      ): Promise<EmailMarketingSenderProfile> => {
        return rb.execute<EmailMarketingSenderProfile>(
          patchAdminEmailMarketingSenderProfilesById,
          {
            path: { id },
            body: {
              data: { id, type: "email-marketing-sender-profile", attributes },
            },
          },
          options,
        );
      },

      /**
       * Delete a sender profile.
       *
       * @param id - The sender profile ID.
       * @param options - Optional request-level overrides.
       * @returns `true` on successful deletion.
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminEmailMarketingSenderProfilesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Trigger DNS validation (SPF/DKIM/DMARC).
       *
       * @param id - The sender profile ID.
       * @param options - Optional request-level overrides.
       * @returns The profile (DNS status may still be pending).
       */
      validateDns: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailMarketingSenderProfile> => {
        return rb.execute<EmailMarketingSenderProfile>(
          patchAdminEmailMarketingSenderProfilesByIdValidateDns,
          {
            path: { id },
            body: { data: { id, type: "email-marketing-sender-profile" } },
          },
          options,
        );
      },

      /**
       * Set a sender profile as the workspace default.
       *
       * @param id - The sender profile ID.
       * @param options - Optional request-level overrides.
       * @returns The profile with `is_default` set to `true`.
       */
      setDefault: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailMarketingSenderProfile> => {
        return rb.execute<EmailMarketingSenderProfile>(
          patchAdminEmailMarketingSenderProfilesByIdSetDefault,
          {
            path: { id },
            body: { data: { id, type: "email-marketing-sender-profile" } },
          },
          options,
        );
      },
    },

    /**
     * Recipients — to/cc/bcc addresses on an outbound email.
     */
    recipients: {
      /**
       * Get a recipient by ID.
       *
       * @param id - The recipient record ID.
       * @param options - Optional request-level overrides.
       * @returns The requested {@link EmailRecipient}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailRecipient> => {
        return rb.execute<EmailRecipient>(
          getAdminEmailRecipientsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Add a recipient to an outbound email.
       *
       * @param attributes - Recipient details. `outbound_email_id` and `email` are required.
       * @param options - Optional request-level overrides.
       * @returns The newly created {@link EmailRecipient}.
       */
      create: async (
        attributes: CreateRecipientAttributes,
        options?: RequestOptions,
      ): Promise<EmailRecipient> => {
        return rb.execute<EmailRecipient>(
          postAdminEmailRecipients,
          { body: { data: { type: "email-recipient", attributes } } },
          options,
        );
      },

      /**
       * Remove a recipient from an outbound email.
       *
       * @param id - The recipient record ID.
       * @param options - Optional request-level overrides.
       * @returns `true` on successful deletion.
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminEmailRecipientsById,
          { path: { id } },
          options,
        );
      },

      /**
       * List all recipients for an outbound email.
       *
       * @param outboundEmailId - The outbound email ID.
       * @param options - Optional request-level overrides.
       * @returns An array of {@link EmailRecipient} records.
       */
      listByEmail: async (
        outboundEmailId: string,
        options?: RequestOptions,
      ): Promise<EmailRecipient[]> => {
        return rb.execute<EmailRecipient[]>(
          getAdminEmailRecipientsEmailByOutboundEmailId,
          { path: { outbound_email_id: outboundEmailId } },
          options,
        );
      },
    },

    /**
     * Inclusions — structured data embedded in email bodies.
     */
    inclusions: {
      /**
       * Get an inclusion by ID.
       *
       * @param id - The inclusion ID.
       * @param options - Optional request-level overrides.
       * @returns The requested {@link EmailInclusion}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailInclusion> => {
        return rb.execute<EmailInclusion>(
          getAdminEmailInclusionsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Attach a structured data inclusion to a draft email.
       *
       * @param attributes - Inclusion details. `outbound_email_id` and `inclusion_type` are required.
       * @param options - Optional request-level overrides.
       * @returns The newly created {@link EmailInclusion}.
       */
      create: async (
        attributes: CreateInclusionAttributes,
        options?: RequestOptions,
      ): Promise<EmailInclusion> => {
        return rb.execute<EmailInclusion>(
          postAdminEmailInclusions,
          { body: { data: { type: "email-inclusion", attributes } } },
          options,
        );
      },

      /**
       * Update an inclusion (PATCH semantics).
       *
       * @param id - The inclusion ID.
       * @param attributes - Fields to update.
       * @param options - Optional request-level overrides.
       * @returns The updated {@link EmailInclusion}.
       */
      update: async (
        id: string,
        attributes: UpdateInclusionAttributes,
        options?: RequestOptions,
      ): Promise<EmailInclusion> => {
        return rb.execute<EmailInclusion>(
          patchAdminEmailInclusionsById,
          {
            path: { id },
            body: { data: { id, type: "email-inclusion", attributes } },
          },
          options,
        );
      },

      /**
       * Remove an inclusion from a draft email.
       *
       * @param id - The inclusion ID.
       * @param options - Optional request-level overrides.
       * @returns `true` on successful deletion.
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminEmailInclusionsById,
          { path: { id } },
          options,
        );
      },

      /**
       * List all inclusions for an outbound email.
       *
       * @param outboundEmailId - The outbound email ID.
       * @param options - Optional request-level overrides.
       * @returns An array of {@link EmailInclusion} records.
       */
      listByEmail: async (
        outboundEmailId: string,
        options?: RequestOptions,
      ): Promise<EmailInclusion[]> => {
        return rb.execute<EmailInclusion[]>(
          getAdminEmailInclusionsEmailByOutboundEmailId,
          { path: { outbound_email_id: outboundEmailId } },
          options,
        );
      },
    },

    /**
     * Tracking Events — open/click/bounce event history. Read-only.
     */
    trackingEvents: {
      /**
       * Get a tracking event by ID.
       *
       * @param id - The tracking event ID.
       * @param options - Optional request-level overrides.
       * @returns The requested {@link EmailTrackingEvent}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailTrackingEvent> => {
        return rb.execute<EmailTrackingEvent>(
          getAdminEmailTrackingEventsById,
          { path: { id } },
          options,
        );
      },

      /**
       * List tracking events for a workspace.
       *
       * @param workspaceId - The workspace ID.
       * @param options - Pagination and request-level overrides.
       * @returns An array of {@link EmailTrackingEvent} records.
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<EmailTrackingEvent[]> => {
        return rb.execute<EmailTrackingEvent[]>(
          getAdminEmailTrackingEventsWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            ...buildPageQuery(options?.page, options?.pageSize),
          },
          options,
        );
      },
    },

    /**
     * Unsubscribers — CAN-SPAM global unsubscribe registry. Read-only.
     */
    unsubscribers: {
      /**
       * Get an unsubscribe record by ID.
       *
       * @param id - The unsubscriber record ID.
       * @param options - Optional request-level overrides.
       * @returns The requested {@link EmailUnsubscriber}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailUnsubscriber> => {
        return rb.execute<EmailUnsubscriber>(
          getAdminEmailMarketingUnsubscribersById,
          { path: { id } },
          options,
        );
      },

      /**
       * List unsubscriber records for a workspace.
       *
       * @param workspaceId - The workspace ID.
       * @param options - Pagination and request-level overrides.
       * @returns An array of {@link EmailUnsubscriber} records.
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<EmailUnsubscriber[]> => {
        return rb.execute<EmailUnsubscriber[]>(
          getAdminEmailMarketingUnsubscribersWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            ...buildPageQuery(options?.page, options?.pageSize),
          },
          options,
        );
      },
    },

    /**
     * Send Limits — daily send quota records.
     */
    sendLimits: {
      /**
       * Get a send limit record by ID.
       *
       * @param id - The send limit record ID.
       * @param options - Optional request-level overrides.
       * @returns The requested {@link EmailSendLimit} record.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<EmailSendLimit> => {
        return rb.execute<EmailSendLimit>(
          getAdminEmailSendLimitsById,
          { path: { id } },
          options,
        );
      },

      /**
       * List send limit records for a workspace (historical).
       *
       * @param workspaceId - The workspace ID.
       * @param options - Pagination and request-level overrides.
       * @returns An array of {@link EmailSendLimit} records.
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<EmailSendLimit[]> => {
        return rb.execute<EmailSendLimit[]>(
          getAdminEmailSendLimitsWorkspaceByWorkspaceId,
          {
            path: { workspace_id: workspaceId },
            ...buildPageQuery(options?.page, options?.pageSize),
          },
          options,
        );
      },
    },

    /**
     * Inbound Addresses — workspace-scoped email addresses for receiving mail.
     */
    inbound: {
      addresses: {
        /**
         * Get an inbound address by ID.
         *
         * @param id - The inbound address ID.
         * @param options - Optional request-level overrides.
         * @returns The requested {@link EmailInboundAddress}.
         */
        get: async (
          id: string,
          options?: RequestOptions,
        ): Promise<EmailInboundAddress> => {
          return rb.execute<EmailInboundAddress>(
            getAdminEmailInboundAddressesById,
            { path: { id } },
            options,
          );
        },

        /**
         * Create a new inbound address.
         *
         * `workspace_id` is an explicit required parameter.
         *
         * @param attributes - Address configuration.
         * @param options - Optional request-level overrides.
         * @returns The newly created {@link EmailInboundAddress}.
         */
        create: async (
          attributes: CreateInboundAddressAttributes,
          options?: RequestOptions,
        ): Promise<EmailInboundAddress> => {
          return rb.execute<EmailInboundAddress>(
            postAdminEmailInboundAddresses,
            {
              body: {
                data: { type: "email-inbound-address", attributes },
              },
            },
            options,
          );
        },

        /**
         * Update an inbound address (PATCH semantics).
         *
         * @param id - The inbound address ID.
         * @param attributes - Fields to update.
         * @param options - Optional request-level overrides.
         * @returns The updated {@link EmailInboundAddress}.
         */
        update: async (
          id: string,
          attributes: UpdateInboundAddressAttributes,
          options?: RequestOptions,
        ): Promise<EmailInboundAddress> => {
          return rb.execute<EmailInboundAddress>(
            patchAdminEmailInboundAddressesById,
            {
              path: { id },
              body: {
                data: { id, type: "email-inbound-address", attributes },
              },
            },
            options,
          );
        },

        /**
         * Delete an inbound address.
         *
         * @param id - The inbound address ID.
         * @param options - Optional request-level overrides.
         * @returns `true` on successful deletion.
         */
        delete: async (id: string, options?: RequestOptions): Promise<true> => {
          return rb.executeDelete(
            deleteAdminEmailInboundAddressesById,
            { path: { id } },
            options,
          );
        },

        /**
         * Rotate the inbound address token (invalidates the old token).
         *
         * @param id - The inbound address ID.
         * @param options - Optional request-level overrides.
         * @returns The address with the new token.
         */
        rotateToken: async (
          id: string,
          options?: RequestOptions,
        ): Promise<EmailInboundAddress> => {
          return rb.execute<EmailInboundAddress>(
            patchAdminEmailInboundAddressesByIdRotateToken,
            {
              path: { id },
              body: { data: { id, type: "email-inbound-address" } },
            },
            options,
          );
        },

        /**
         * Disable an inbound address.
         *
         * @param id - The inbound address ID.
         * @param options - Optional request-level overrides.
         * @returns The address with `is_active` set to `false`.
         */
        disable: async (
          id: string,
          options?: RequestOptions,
        ): Promise<EmailInboundAddress> => {
          return rb.execute<EmailInboundAddress>(
            patchAdminEmailInboundAddressesByIdDisable,
            {
              path: { id },
              body: { data: { id, type: "email-inbound-address" } },
            },
            options,
          );
        },

        /**
         * Re-enable a disabled inbound address.
         *
         * @param id - The inbound address ID.
         * @param options - Optional request-level overrides.
         * @returns The address with `is_active` set to `true`.
         */
        enable: async (
          id: string,
          options?: RequestOptions,
        ): Promise<EmailInboundAddress> => {
          return rb.execute<EmailInboundAddress>(
            patchAdminEmailInboundAddressesByIdEnable,
            {
              path: { id },
              body: { data: { id, type: "email-inbound-address" } },
            },
            options,
          );
        },

        /**
         * List all inbound addresses in a workspace.
         *
         * @param workspaceId - The workspace ID.
         * @param options - Pagination and request-level overrides.
         * @returns An array of {@link EmailInboundAddress} records.
         */
        listByWorkspace: async (
          workspaceId: string,
          options?: { page?: number; pageSize?: number } & RequestOptions,
        ): Promise<EmailInboundAddress[]> => {
          return rb.execute<EmailInboundAddress[]>(
            getAdminEmailInboundAddressesWorkspaceByWorkspaceId,
            {
              path: { workspace_id: workspaceId },
              ...buildPageQuery(options?.page, options?.pageSize),
            },
            options,
          );
        },
      },

      /** Received — read-only log of received inbound emails. */
      received: {
        /**
         * Get a received inbound email by ID.
         *
         * @param id - The inbound email ID.
         * @param options - Optional request-level overrides.
         * @returns The requested {@link EmailInboundEmail}.
         */
        get: async (
          id: string,
          options?: RequestOptions,
        ): Promise<EmailInboundEmail> => {
          return rb.execute<EmailInboundEmail>(
            getAdminEmailInboundReceivedById,
            { path: { id } },
            options,
          );
        },

        /**
         * List received emails by inbound address.
         *
         * @param addressId - The inbound address ID.
         * @param options - Pagination and request-level overrides.
         * @returns An array of {@link EmailInboundEmail} records.
         */
        listByAddress: async (
          addressId: string,
          options?: { page?: number; pageSize?: number } & RequestOptions,
        ): Promise<EmailInboundEmail[]> => {
          return rb.execute<EmailInboundEmail[]>(
            getAdminEmailInboundReceivedAddressByInboundAddressId,
            {
              path: { inbound_address_id: addressId },
              ...buildPageQuery(options?.page, options?.pageSize),
            },
            options,
          );
        },

        /**
         * List all received emails in a workspace.
         *
         * @param workspaceId - The workspace ID.
         * @param options - Pagination and request-level overrides.
         * @returns An array of {@link EmailInboundEmail} records.
         */
        listByWorkspace: async (
          workspaceId: string,
          options?: { page?: number; pageSize?: number } & RequestOptions,
        ): Promise<EmailInboundEmail[]> => {
          return rb.execute<EmailInboundEmail[]>(
            getAdminEmailInboundReceivedWorkspaceByWorkspaceId,
            {
              path: { workspace_id: workspaceId },
              ...buildPageQuery(options?.page, options?.pageSize),
            },
            options,
          );
        },
      },
    },
  };
}
